import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import LandingPage from "@/pages/LandingPage";
import ServiceDetailPage from "@/pages/ServiceDetailPage";
import ServicesHubPage from "@/pages/ServicesHubPage";
import ServiceAreasPage from "@/pages/ServiceAreasPage";
import EstimateCalculatorPage from "@/pages/EstimateCalculatorPage";
import GalleryPage from "@/pages/GalleryPage";
import ReviewsPage from "@/pages/ReviewsPage";
import AboutPage from "@/pages/AboutPage";
import FaqPage from "@/pages/FaqPage";
import ContactPage from "@/pages/ContactPage";
import PrivacyPage from "@/pages/PrivacyPage";
import TermsPage from "@/pages/TermsPage";
import ChatWidget from "@/components/ChatWidget";
import MobileActionBar from "@/components/MobileActionBar";
import AnnouncementBanner from "@/components/AnnouncementBanner";
import ScrollToTop from "@/components/ScrollToTop";
import LoginPage from "@/pages/LoginPage";
import AdminQuotesPage from "@/pages/AdminQuotesPage";
import AdminJobsPage from "@/pages/AdminJobsPage";
import AdminCalendarPage from "@/pages/AdminCalendarPage";
import { useAuth } from "@/context/AuthContext";

function ProtectedRoute({ children, requireAdmin }) {
  const { user, checked } = useAuth();
  if (!checked) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50">
        <div className="w-10 h-10 border-2 border-stone-300 border-t-brand-600 rounded-full animate-spin" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (requireAdmin && user.role !== "admin") return <Navigate to="/dashboard" replace />;
  return children;
}

function App() {
  return (
    <HelmetProvider>
      <div className="App">
        <BrowserRouter>
          <ScrollToTop />
          <AnnouncementBanner />
          <Routes>
            <Route path="/" element={<LandingPage />} />

            {/* Marketing & SEO pages */}
            <Route path="/pressure-washing-services/" element={<ServicesHubPage />} />
            <Route path="/pressure-washing-services" element={<Navigate to="/pressure-washing-services/" replace />} />
            <Route path="/pressure-washing-estimate-calculator/" element={<EstimateCalculatorPage />} />
            <Route path="/pressure-washing-estimate-calculator" element={<Navigate to="/pressure-washing-estimate-calculator/" replace />} />
            <Route path="/service-areas/" element={<ServiceAreasPage />} />
            <Route path="/service-areas" element={<Navigate to="/service-areas/" replace />} />

            {/* Company / info pages */}
            <Route path="/pressure-washing-gallery/" element={<GalleryPage />} />
            <Route path="/pressure-washing-gallery" element={<Navigate to="/pressure-washing-gallery/" replace />} />
            <Route path="/reviews/" element={<ReviewsPage />} />
            <Route path="/reviews" element={<Navigate to="/reviews/" replace />} />
            <Route path="/about/" element={<AboutPage />} />
            <Route path="/about" element={<Navigate to="/about/" replace />} />
            <Route path="/faq/" element={<FaqPage />} />
            <Route path="/faq" element={<Navigate to="/faq/" replace />} />
            <Route path="/contact/" element={<ContactPage />} />
            <Route path="/contact" element={<Navigate to="/contact/" replace />} />
            <Route path="/privacy-policy/" element={<PrivacyPage />} />
            <Route path="/privacy-policy" element={<Navigate to="/privacy-policy/" replace />} />
            <Route path="/terms-and-estimate-disclaimer/" element={<TermsPage />} />
            <Route path="/terms-and-estimate-disclaimer" element={<Navigate to="/terms-and-estimate-disclaimer/" replace />} />

            {/* SEO-friendly service pages */}
            <Route path="/:slug/" element={<ServiceDetailPage />} />
            <Route path="/:slug" element={<ServiceDetailPage />} />

            {/* Legacy service URLs still work */}
            <Route path="/services/:slug" element={<ServiceDetailPage />} />

            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<Navigate to="/" replace />} />
            <Route path="/dashboard" element={<Navigate to="/" replace />} />
            <Route path="/dashboard/jobs/:jobId" element={<Navigate to="/" replace />} />
            <Route
              path="/admin/quotes"
              element={
                <ProtectedRoute requireAdmin>
                  <AdminQuotesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/jobs"
              element={
                <ProtectedRoute requireAdmin>
                  <AdminJobsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/calendar"
              element={
                <ProtectedRoute requireAdmin>
                  <AdminCalendarPage />
                </ProtectedRoute>
              }
            />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <ChatWidget />
          <MobileActionBar />
        </BrowserRouter>
      </div>
    </HelmetProvider>
  );
}

export default App;
